// A generic, lazy, keyboard-navigable, VSCode-style expandable tree.
//
// Every part of the debugger — locals, the transaction's read/write sets, and the
// threads with their call stacks — is one of these. A node's children are a thunk
// so the tree only ever walks what the user opens, which is what lets it explore
// a deep (or cyclic) object graph without ever materializing the whole thing.
//
// Expansion state is keyed by node *path* and persists across re-renders, so
// stepping through the trace keeps whatever you had opened open; a node's
// `defaultExpanded` decides its initial state until you toggle it. The whole tree
// is one focusable region driven by the arrow keys, mirroring the trace view.

import { useEffect, useRef, useState } from "react";

export interface TreeNode {
  /** Stable id among siblings; combined into a path for expansion/focus state. */
  id: string;
  /** Row content (already formatted — name/value spans, etc.). */
  label: React.ReactNode;
  /** Children, computed lazily on expand. Absent → a leaf (no twisty). */
  children?: () => TreeNode[];
  /**
   * Known to have no children, without the thunk being called.
   *
   * A lazy tree cannot tell a node whose children are an empty list from one
   * whose children it simply hasn't walked, so by default anything with a thunk
   * gets a twisty. That is right for a tree over an object graph, where having a
   * thunk means having contents; it is wrong for one whose nodes all take the
   * same shape and only some turn out to be ends. Such a source works out its own
   * leaves and says so here.
   */
  leaf?: boolean;
  /**
   * How many children this node has, shown on the row — so a node says how much
   * is behind it before you open it. `null` means "not worked out yet" (a source
   * that pages its answers in), which shows nothing and keeps the twisty; `0`
   * with no `leaf` would be a contradiction and is treated as unknown.
   */
  count?: number | null;
  /** Initial expansion, until the user toggles this node. */
  defaultExpanded?: boolean;
  /** Marks the row as selected (e.g. the current call-stack frame). */
  selected?: boolean;
  /** Extra classes on the row, for state a tree wants to show that isn't
   *  selection — the timeline tree marks the path the current run takes. */
  className?: string;
  /** Activating the row (Enter, or a click) runs this; else it toggles. Also
   *  fired when the arrow keys move focus onto the row, so navigating the tree
   *  drives selection live — the way arrowing the trace view does. */
  onSelect?: () => void;
}

interface TreeProps {
  nodes: TreeNode[];
  ariaLabel?: string;
  /** Empty-state text when there are no nodes. */
  empty?: string;
  /** For pure-inspection trees (locals, txn sets) that carry no app-level
   *  selection: render the keyboard-cursor row as the selection, so its
   *  "current row" reads identically to a tree that does have one (the
   *  threads/call-stack pane) — same accent-blue-when-focused / grey-when-blurred
   *  fill instead of the lighter cursor ring. Trees with their own `selected`
   *  rows leave this off, keeping cursor and selection visually distinct. */
  focusIsSelection?: boolean;
  /** Pixels of indent per level. The default suits trees a few levels deep; a
   *  tree whose *depth* is the point (the timeline tree is one node per
   *  scheduling decision, so a short program is already a dozen deep) wants
   *  less, or the labels are pushed off the side of a sidebar. */
  indent?: number;
}

interface Row {
  path: string;
  node: TreeNode;
  depth: number;
  expandable: boolean;
  expanded: boolean;
}

/** Flatten the tree to the currently-visible rows, in display order. Lazy child
 *  thunks are only called for expanded nodes, so unopened subtrees stay unwalked. */
function flatten(
  nodes: TreeNode[],
  overrides: Map<string, boolean>,
  depth: number,
  prefix: string,
  out: Row[]
): void {
  for (const node of nodes) {
    const path = `${prefix}/${node.id}`;
    const expandable = !!node.children && !node.leaf;
    const expanded = expandable && (overrides.has(path) ? overrides.get(path)! : !!node.defaultExpanded);
    out.push({ path, node, depth, expandable, expanded });
    if (expanded) flatten(node.children!(), overrides, depth + 1, path, out);
  }
}

function parentOf(path: string): string {
  return path.slice(0, path.lastIndexOf("/"));
}

export function Tree({ nodes, ariaLabel, empty, focusIsSelection, indent = 14 }: TreeProps) {
  // Per-path user overrides of the default expansion; persists across snapshots.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(() => new Map());
  const [focus, setFocus] = useState<string | null>(null);
  const scrollRef = useRef<HTMLUListElement>(null);

  const setExpanded = (path: string, val: boolean) =>
    setOverrides((prev) => new Map(prev).set(path, val));

  const rows: Row[] = [];
  flatten(nodes, overrides, 0, "", rows);

  // Where the cursor actually is. A tree whose shape depends on live data can
  // have the focused row replaced under it — the timeline tree re-marks its
  // whole spine every time a node is chosen — and a cursor that simply vanished
  // would send the next arrow key back to the top. Fall back to the nearest
  // surviving ancestor, so the cursor lands on the branch the row was in.
  const focusPath = ((): string | null => {
    if (!focus) return null;
    for (let p = focus; p; p = parentOf(p)) {
      if (rows.some((r) => r.path === p)) return p;
    }
    return null;
  })();

  // Keep the focused row scrolled into view as the arrow keys move it.
  useEffect(() => {
    if (!focusPath) return;
    scrollRef.current
      ?.querySelector(`[data-path="${CSS.escape(focusPath)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [focusPath]);

  const focusRow = (i: number): void => {
    const row = rows[i];
    if (!row) return;
    setFocus(row.path);
    // Move-selects, like arrowing the trace view: a selectable row activates on focus.
    row.node.onSelect?.();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (rows.length === 0) return;
    const idx = rows.findIndex((r) => r.path === focusPath);
    if (idx < 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        focusRow(0);
      }
      return;
    }
    const row = rows[idx]!;
    switch (e.key) {
      // Up and down walk what is on screen: the next row down is the node's
      // sibling when it is closed and its first child when it is open. Right and
      // left are what change that — they open and close.
      case "ArrowDown":
        e.preventDefault();
        focusRow(Math.min(idx + 1, rows.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        focusRow(Math.max(idx - 1, 0));
        break;
      case "ArrowRight":
        e.preventDefault();
        if (row.expandable && !row.expanded) setExpanded(row.path, true);
        else if (row.expandable && row.expanded) focusRow(idx + 1); // step into first child
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (row.expandable && row.expanded) setExpanded(row.path, false);
        else {
          const pi = rows.findIndex((r) => r.path === parentOf(row.path));
          if (pi >= 0) focusRow(pi);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (row.node.onSelect) row.node.onSelect();
        else if (row.expandable) setExpanded(row.path, !row.expanded);
        break;
    }
  };

  if (rows.length === 0 && empty) return <div className="tree-empty">{empty}</div>;

  return (
    <ul
      className="tree"
      role="tree"
      aria-label={ariaLabel}
      tabIndex={0}
      ref={scrollRef}
      onKeyDown={onKeyDown}
    >
      {rows.map((row) => (
        <li key={row.path} role="treeitem" aria-expanded={row.expandable ? row.expanded : undefined}>
          <div
            data-path={row.path}
            className={
              "tree-row" +
              (row.node.selected || (focusIsSelection && row.path === focusPath) ? " sel" : "") +
              (row.path === focusPath ? " focus" : "") +
              (row.node.className ? ` ${row.node.className}` : "")
            }
            style={{ paddingLeft: row.depth * indent + 4 }}
            onClick={() => {
              setFocus(row.path);
              if (row.node.onSelect) row.node.onSelect();
              else if (row.expandable) setExpanded(row.path, !row.expanded);
            }}
          >
            <span
              className={"tree-twisty" + (row.expandable ? (row.expanded ? " open" : " closed") : " leaf")}
              onClick={(e) => {
                if (!row.expandable) return;
                e.stopPropagation();
                setFocus(row.path);
                setExpanded(row.path, !row.expanded);
              }}
            >
              {row.expandable ? "›" : ""}
            </span>
            <span className="tree-label">{row.node.label}</span>
            {row.expandable && !!row.node.count && (
              <span className="tree-count" title={`${row.node.count} directly under this`}>
                {row.node.count}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
